import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dialog } from "@/components/ui/design-system";
import { DocumentViewer } from "@/components/ui/document-workspace";
import { api, errorMessage } from "@/lib/api";
import { documentUrl, equipmentProofPath } from "@/lib/oprema";

/** Equipment issuers open an authenticated PDF without entering the archive. */
export function EquipmentProofLink({ workerId, documentId, children = "Otvori potvrdu", className = "inline-flex min-h-11 items-center underline" }: {
  workerId: string; documentId: string; children?: ReactNode; className?: string;
}) {
  const { can } = useAuth();
  const [preview, setPreview] = useState<{ key: string; url: string } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const request = useRef<AbortController | null>(null);
  const resource = useRef<string | null>(null);
  const allowed = can("oprema", "create");
  const archive = can("dokument", "read");
  const key = `${workerId}/${documentId}/${allowed}/${archive}`;
  useEffect(() => () => {
    request.current?.abort();
    if (resource.current) URL.revokeObjectURL(resource.current);
    resource.current = null;
  }, [key]);
  async function open() {
    if (busy) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError("");
    try {
      const response = await api.get<Blob>(`${equipmentProofPath(workerId, documentId)}/content`, {
        responseType: "blob", signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        if (resource.current) URL.revokeObjectURL(resource.current);
        resource.current = URL.createObjectURL(response.data);
        setPreview({ key, url: resource.current });
      }
    } catch (cause) {
      if (!controller.signal.aborted) setError(errorMessage(cause, "Potvrda nije dostupna."));
    } finally {
      if (request.current === controller) setBusy(false);
    }
  }
  if (archive) return <Link className={className} to={documentUrl(workerId, documentId)}>{children}</Link>;
  if (!allowed) return null;
  return <>
    <button type="button" className={className} disabled={busy} onClick={() => void open()}>{children}</button>
    {error && <span role="alert">{error}</span>}
    {preview?.key === key && <Dialog fullscreen title="Potvrda o primitku opreme" onClose={() => {
        if (resource.current) URL.revokeObjectURL(resource.current);
        resource.current = null;
        setPreview(null);
      }}>
      <DocumentViewer title="Potvrda o primitku opreme" url={preview.url} mime="application/pdf" />
    </Dialog>}
  </>;
}
